import { ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';

import { PreviewImage, PreviewSidebar, PreviewStatusBadge } from './PreviewSidebar';
import { formatPreviewDate } from './previewModel';
import type { PreviewCategory, PreviewPost } from './previewModel';

interface BlogListPreviewProps {
  /** Các bài của trang đang xem — đã lọc danh mục và chia trang. */
  readonly posts: readonly PreviewPost[];
  readonly page: number;
  readonly totalPages: number;
  readonly onPage: (page: number) => void;
  /** Tổng số bài, cho dòng "Tất cả bài viết" ở cột bên. */
  readonly totalCount: number;
  readonly categories: readonly PreviewCategory[];
  readonly recent: readonly PreviewPost[];
  readonly activeCategory: string | null;
  readonly onSelectCategory: (slug: string | null) => void;
  readonly onOpen: (post: PreviewPost) => void;
}

/**
 * Trang danh sách blog kiểu RTC: dải tiêu đề, nút lọc danh mục, thẻ bài ngang (ảnh trái, chữ phải), chia trang,
 * cột bên phải.
 */
export function BlogListPreview({
  posts,
  page,
  totalPages,
  onPage,
  totalCount,
  categories,
  recent,
  activeCategory,
  onSelectCategory,
  onOpen,
}: BlogListPreviewProps): ReactNode {
  const activeName = categories.find((category) => category.slug === activeCategory)?.name;

  return (
    <>
      <div className="mv-hero">
        <div className="mv-crumb-light">Trang chủ / Blog{activeName === undefined ? '' : ` / ${activeName}`}</div>
        <h1>Blog</h1>
        <p>Kiến thức, giải pháp và tin tức về Machine Vision, tự động hóa từ đội ngũ MVA Lab.</p>
      </div>

      <div className="mv-container">
        <main>
          <h2 className="mv-section-title">
            <span>{activeName ?? 'Bài viết mới'}</span>
          </h2>

          <div className="mv-pills">
            <span className={`mv-pill${activeCategory === null ? ' active' : ''}`} onClick={() => { onSelectCategory(null); }}>
              Tất cả
            </span>
            {categories.map((category) => (
              <span
                key={category.slug}
                className={`mv-pill${activeCategory === category.slug ? ' active' : ''}`}
                onClick={() => { onSelectCategory(category.slug); }}
              >
                {category.name}
              </span>
            ))}
          </div>

          {posts.length === 0 ? (
            <div className="mv-empty">Chưa có bài viết nào.</div>
          ) : (
            posts.map((post) => (
              <article key={post.id} className="mv-card" onClick={() => { onOpen(post); }}>
                <div className="mv-card-img">
                  <PreviewStatusBadge post={post} />
                  <PreviewImage url={post.coverImageUrl} alt={post.coverImageAlt} />
                </div>
                <div className="mv-card-body">
                  {post.categoryName === null ? null : <span className="mv-badge">{post.categoryName}</span>}
                  <h3>{post.title}</h3>
                  <div className="mv-meta">
                    <span>
                      <ClockCircleOutlined className="accent" />
                      {formatPreviewDate(post.date)}
                    </span>
                    {post.authorName === null ? null : (
                      <span>
                        <UserOutlined className="accent" />
                        {post.authorName}
                      </span>
                    )}
                  </div>
                  <p className="mv-excerpt">{post.excerpt}</p>
                  <span className="mv-more">Đọc tiếp →</span>
                </div>
              </article>
            ))
          )}

          {totalPages > 1 ? (
            <div className="mv-pages">
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((number) => (
                <span
                  key={number}
                  className={`mv-page${number === page ? ' active' : ''}`}
                  onClick={() => { onPage(number); }}
                >
                  {number.toString()}
                </span>
              ))}
            </div>
          ) : null}
        </main>

        <PreviewSidebar
          totalCount={totalCount}
          categories={categories}
          recent={recent}
          activeCategory={activeCategory}
          onSelectCategory={onSelectCategory}
          onOpen={onOpen}
        />
      </div>
    </>
  );
}
